import {head} from 'ramda';

import {FinancingRateMatrixCell, SaveSaleVehicleFinancingBody} from '@dms/api/vehicle';

/**
 * Orders cells the way the matrix reads: the shortest term first, then the lowest down payment,
 * then the lowest balloon payment. A cell without a balloon payment sorts before every bracket.
 */
function compareFinancingCells(left: FinancingRateMatrixCell, right: FinancingRateMatrixCell) {
  return (
    left.term - right.term ||
    left.downPayment - right.downPayment ||
    (left.balloonPayment ?? -1) - (right.balloonPayment ?? -1)
  );
}

/**
 * Points the active variant at a cell that exists, so a body never claims a coordinate its matrix
 * no longer holds. An empty matrix leaves the active variant unset.
 *
 * When the claimed coordinate is gone, the fallback is the first cell in matrix order, not the
 * first cell the array happens to hold: the same matrix must always fall back to the same variant,
 * whatever order edits, bulk applies and removals left the cells in.
 */
export function ensureActiveFinancingCell(body: SaveSaleVehicleFinancingBody) {
  const activeCell =
    body.rateMatrix.cells.find(
      (cell) =>
        cell.term === body.activeTerm &&
        cell.downPayment === body.activeDownPayment &&
        cell.balloonPayment === body.activeBalloonPayment
    ) ?? head([...body.rateMatrix.cells].sort(compareFinancingCells));

  return {
    ...body,
    activeTerm: activeCell?.term ?? null,
    activeDownPayment: activeCell?.downPayment ?? null,
    activeBalloonPayment: activeCell?.balloonPayment ?? null,
  };
}
